import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Clock, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = 'https://www.obiquistesolutions.com';

// Helper component to safely inject JSON-LD
const JsonLd = ({ data }) => {
  if (!data) return null;
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data).replace(/</g, '\\u003c') }}
    />
  );
};

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Memoize JSON-LD schema
  const blogListSchema = useMemo(() => {
    if (blogs.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Obiquiste Solutions Blog",
      "description": "Expert guides on selling on Amazon, Flipkart, Blinkit & Swiggy Instamart. Tips, strategies & marketplace insights for e-commerce sellers in India.",
      "url": SITE_URL + "/blog",
      "publisher": {
        "@type": "Organization",
        "name": "Obiquiste Solutions",
        "logo": {
          "@type": "ImageObject",
          "url": SITE_URL + "/android-chrome-512x512.png"
        }
      },
      "blogPost": blogs.map(blog => ({
        "@type": "BlogPosting",
        "headline": blog.title,
        "description": blog.excerpt,
        "url": SITE_URL + "/blog/" + blog.slug,
        "datePublished": blog.created_at,
        "author": {
          "@type": "Organization",
          "name": blog.author || "Obiquiste Solutions"
        }
      }))
    };
  }, [blogs]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Helmet>
          <title>E-commerce and Quick Commerce Blog | Obiquiste Solutions</title>
          <meta name="description" content="Expert guides on selling on Amazon, Flipkart, Blinkit and Swiggy Instamart. Tips, strategies and marketplace insights for e-commerce sellers in India." />
          <link rel="canonical" href={SITE_URL + "/blog"} />
        </Helmet>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading blogs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24">
      <Helmet>
        <title>E-commerce and Quick Commerce Blog | Obiquiste Solutions</title>
        <meta name="description" content="Expert guides on selling on Amazon, Flipkart, Blinkit and Swiggy Instamart. Tips, strategies and marketplace insights for e-commerce sellers in India." />
        <link rel="canonical" href={SITE_URL + "/blog"} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SITE_URL + "/blog"} />
        <meta property="og:title" content="E-commerce and Quick Commerce Blog | Obiquiste Solutions" />
        <meta property="og:description" content="Expert guides on selling on Amazon, Flipkart, Blinkit and Swiggy Instamart. Tips, strategies and marketplace insights for e-commerce sellers in India." />
        <meta property="og:image" content={SITE_URL + "/android-chrome-512x512.png"} />
        <meta property="og:site_name" content="Obiquiste Solutions" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={SITE_URL + "/blog"} />
        <meta name="twitter:title" content="E-commerce and Quick Commerce Blog | Obiquiste Solutions" />
        <meta name="twitter:description" content="Expert guides on selling on Amazon, Flipkart, Blinkit and Swiggy Instamart. Tips, strategies and marketplace insights for e-commerce sellers in India." />
        <meta name="twitter:image" content={SITE_URL + "/android-chrome-512x512.png"} />
      </Helmet>
      
      <JsonLd data={blogListSchema} />
      
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 to-blue-50/50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 data-testid="blog-heading" className="font-heading text-5xl md:text-7xl font-bold tracking-tight mb-6">
              Blog & <span className="text-gradient">Insights</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600">
              Expert tips, strategies, and insights for e-commerce success.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {blogs.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-slate-600">No blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog, idx) => (
                <motion.div
                  key={blog.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow"
                  data-testid={`blog-item-${idx}`}
                >
                  {blog.image_url && (
                    <img
                      src={blog.image_url}
                      alt={blog.title}
                      className="w-full h-56 object-cover"
                    />
                  )}
                  <div className="p-6">
                    <div className="flex items-center text-sm text-slate-500 mb-3">
                      <Clock size={16} className="mr-2" />
                      {new Date(blog.created_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </div>
                    <h3 className="font-heading text-xl font-semibold mb-3">{blog.title}</h3>
                    <p className="text-slate-600 mb-4 line-clamp-3">{blog.excerpt}</p>
                    <Link to={"/blog/" + blog.slug}>
                      <Button variant="outline" className="w-full">
                        Read More
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(API + "/blogs/" + slug);
      setBlog(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update document title directly as fallback
  useEffect(() => {
    if (blog) {
      const titleText = blog.meta_title || blog.title || "Blog Post";
      document.title = titleText + " | Obiquiste Solutions";
    } else if (loading) {
      document.title = "Loading Blog | Obiquiste Solutions";
    }
  }, [blog, loading]);

  // Memoize JSON-LD schema
  const blogSchema = useMemo(() => {
    if (!blog) return null;
    return {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": SITE_URL + "/blog/" + blog.slug
      },
      "headline": blog.meta_title || blog.title,
      "description": blog.meta_description || blog.excerpt,
      "image": blog.image_url || SITE_URL + "/android-chrome-512x512.png",
      "author": {
        "@type": "Organization",
        "name": blog.author || "Obiquiste Solutions",
        "url": SITE_URL
      },
      "publisher": {
        "@type": "Organization",
        "name": "Obiquiste Solutions",
        "logo": {
          "@type": "ImageObject",
          "url": SITE_URL + "/android-chrome-512x512.png"
        }
      },
      "datePublished": blog.created_at,
      "dateModified": blog.updated_at || blog.created_at,
      "url": SITE_URL + "/blog/" + blog.slug
    };
  }, [blog]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Blog post not found</h2>
          <Link to="/blog">
            <Button>Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Extract values as plain strings with null safety
  const titleText = String(blog?.meta_title || blog?.title || "Blog Post");
  const descText = String(blog?.meta_description || blog?.excerpt || "Read this article on Obiquiste Solutions blog");
  const urlText = SITE_URL + "/blog/" + (blog?.slug || slug);
  const imageUrl = blog?.image_url || SITE_URL + "/android-chrome-512x512.png";
  const authorText = String(blog?.author || "Obiquiste Solutions");
  const publishedDate = String(blog?.created_at || "");
  const modifiedDate = String(blog?.updated_at || blog?.created_at || "");

  // Create title string once to ensure it's a plain string
  const fullTitle = titleText + " | Obiquiste Solutions";

  return (
    <div className="min-h-screen pt-24">
      <Helmet>
        <meta name="title" content={fullTitle} />
        <meta name="description" content={descText} />
        <link rel="canonical" href={urlText} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={urlText} />
        <meta property="og:title" content={titleText} />
        <meta property="og:description" content={descText} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:site_name" content="Obiquiste Solutions" />
        <meta property="article:published_time" content={publishedDate} />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="article:author" content={authorText} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={urlText} />
        <meta name="twitter:title" content={titleText} />
        <meta name="twitter:description" content={descText} />
        <meta name="twitter:image" content={imageUrl} />
      </Helmet>
      
      <JsonLd data={blogSchema} />
      
      <article className="py-16">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <Link to="/blog" className="inline-flex items-center text-primary hover:underline mb-8">
            <ArrowLeft size={20} className="mr-2" />
            Back to Blog
          </Link>
          
          {blog.image_url && (
            <img
              src={blog.image_url}
              alt={blog.title}
              className="w-full h-96 object-cover rounded-2xl mb-8"
            />
          )}

          <div className="mb-8">
            <h1 data-testid="blog-detail-title" className="font-heading text-4xl md:text-5xl font-bold mb-4">
              {blog.title}
            </h1>
            <div className="flex items-center text-slate-600">
              <span className="mr-4">{blog.author}</span>
              <span>•</span>
              <span className="ml-4">
                {new Date(blog.created_at).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
          </div>

          <div
            data-testid="blog-content"
            className="prose prose-lg prose-slate max-w-none prose-headings:font-heading prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-xl prose-h3:font-semibold prose-h3:mt-8 prose-h3:mb-3 prose-p:text-slate-700 prose-p:leading-relaxed prose-strong:text-slate-900 prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-table:border-collapse prose-th:bg-slate-100 prose-th:p-3 prose-th:text-left prose-th:border prose-th:border-slate-300 prose-td:p-3 prose-td:border prose-td:border-slate-200 prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-li:text-slate-700 prose-ul:my-4 prose-ol:my-4"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {blog.content}
            </ReactMarkdown>
          </div>
        </div>
      </article>
    </div>
  );
};

export { BlogList, BlogDetail };
